import { vec3, vec4 } from "gl-matrix"
import {
	prophotoToSrgb,
	applyTransform,
	rgbToHsv,
	applyTransformToOneDimension,
	rgbToSat,
} from "./color-space"
import { ColorSpaceCpu, renderCpu, renderHSLAndColorGrading, RenderOptions } from "./renderer-cpu"
import { Adjustments, PointColorHSL } from "@/core/types"
import { RGBToHSV } from "@/core/utils/color-conversion"
import { getDefaultRelativeShiftRange, getHSVRelativeShiftInRange } from "@/components/HSL/utils"
import { uuid } from "@/utils/uuid"
import { clamp } from "@/utils/math"
import { getWeightedDiff, getWeightedMeanRgb, logColorMatch, wrapHueDiff } from "./utils"
import { extractColors, getColorAndWeight, getRgbData } from "./lut-utils"
import { PixelData } from "@/core/utils/image-data"
import { getGrayMeanRgbs, getGrayPixelIndices } from "./color-grading"

export interface ColorCluster {
	center: vec3
	colors: vec3[]
	indices: number[]
	weightSum: number
	weights?: number[]
	centerInputProphoto?: vec3
	centerTargetProphoto?: vec3
	diffToTarget?: number
}

interface Pixel {
	index: number
	color: vec3
	weight: number
}

export function getInitialColorClusters(options: {
	inputProphoto: Float32Array
	pixelWeights: number[]
	threshold: number
	inputColorSpace: ColorSpaceCpu
	satThreshold?: number
	lumThreshold?: number
}): ColorCluster[] {
	const {
		inputProphoto,
		pixelWeights,
		threshold,
		inputColorSpace,
		satThreshold = 0.1,
		lumThreshold = 0.1,
	} = options
	const lastIndex = pixelWeights.findIndex((weight) => weight < 0.0005)
	const colors = inputProphoto.slice(0, lastIndex * 3)
	const srgbs =
		inputColorSpace === ColorSpaceCpu.sRGB ? colors : applyTransform(colors, prophotoToSrgb)
	const hsvs = applyTransform(srgbs, rgbToHsv)
	const indices: number[] = []
	for (let i = 0; i < hsvs.length / 3; i++) {
		const sat = hsvs[i * 3 + 1]
		const lum = hsvs[i * 3 + 2]
		if (sat && lum && sat >= satThreshold && lum >= lumThreshold) {
			indices.push(i)
		}
	}
	const pixels: Pixel[] = []
	for (let i = 0; i < indices.length; i++) {
		const index = indices[i] as number
		pixels.push({
			index,
			color: [
				srgbs[index * 3] as number,
				srgbs[index * 3 + 1] as number,
				srgbs[index * 3 + 2] as number,
			],
			weight: pixelWeights[index] as number,
		})
	}

	// Cluster colors - sorted by weight
	const clusters = clusterColors(pixels, 100, threshold)
	logColorMatch("initial clusters", clusters.length)
	return clusters
}

function getChangedColorClusters(
	inputProphoto: Float32Array,
	targetProphoto: Float32Array,
	pixelWeights: number[],
	maxClusters: number,
): ColorCluster[] {
	const clusters = getInitialColorClusters({
		inputProphoto,
		pixelWeights,
		threshold: 0.1,
		inputColorSpace: ColorSpaceCpu.proPhoto,
	})

	// Get the clusters that have different input and target colors
	const changedClusters = getChangedClusters({
		clusters,
		inputProphoto,
		targetProphoto,
		pixelWeights,
		diffThreshold: 0.045,
		maxClusters,
	})

	return changedClusters
}

function clusterColors(pixels: Pixel[], maxClusters: number, threshold: number): ColorCluster[] {
	if (pixels.length === 0) return []
	if (pixels.length === 1) {
		const color = pixels[0] as Pixel
		return [
			{
				center: [color.color[0], color.color[1], color.color[2]],
				colors: [color.color],
				indices: [color.index],
				weightSum: color.weight,
			},
		]
	}

	// Initialize clusters with the first color
	const firstColor = pixels[0] as Pixel
	const clusters: ColorCluster[] = [
		{
			center: [firstColor.color[0], firstColor.color[1], firstColor.color[2]],
			colors: [firstColor.color],
			indices: [firstColor.index],
			weightSum: firstColor.weight,
		},
	]

	// Assign each color to nearest cluster or create new one
	for (let i = 1; i < pixels.length; i++) {
		const pixel = pixels[i] as Pixel
		const colorVec = pixel.color
		const index = pixel.index
		if (!colorVec || index === -1) continue

		// Find nearest cluster
		let minDistance = Infinity
		let nearestCluster = -1

		for (let j = 0; j < clusters.length; j++) {
			const distance = colorDifference(colorVec, clusters[j]?.center as vec3)
			if (distance < minDistance) {
				minDistance = distance
				nearestCluster = j
			}
		}

		// Create new cluster if color is too different and we haven't reached max clusters
		if (minDistance > threshold && clusters.length < maxClusters) {
			clusters.push({
				center: [...colorVec],
				colors: [colorVec],
				indices: [index],
				weightSum: pixel.weight,
			})
		} else {
			// Add to nearest cluster
			const cluster = clusters[nearestCluster]
			if (!cluster) continue
			cluster.colors.push(colorVec)
			cluster.indices.push(index)

			// Update cluster center (weighted mean of all colors)
			const oldCenter = cluster.center
			cluster.center = oldCenter.map(
				(v, i) => v * cluster.weightSum + (colorVec[i] as number) * pixel.weight,
			) as vec3
			cluster.weightSum = cluster.weightSum + pixel.weight
			cluster.center = cluster.center.map((v) => v / cluster.weightSum) as vec3
		}
	}

	// Sort clusters by weight sum
	return clusters.sort((a, b) => b.weightSum - a.weightSum)
}

const centerSrgb = vec3.create()
const centerTargetSrgb = vec3.create()
const centerSrgbDiff = vec3.create()

function getChangedClusters({
	clusters,
	inputProphoto,
	targetProphoto,
	pixelWeights,
	diffThreshold,
	maxClusters,
}: {
	clusters: ColorCluster[]
	inputProphoto: Float32Array
	targetProphoto: Float32Array
	pixelWeights: number[]
	diffThreshold: number
	maxClusters: number
}): ColorCluster[] {
	const result: ColorCluster[] = []

	for (const cluster of clusters) {
		if (result.length >= maxClusters) break
		if (cluster.weightSum < 0.002) break

		const { centerInput, centerTarget } = getWeightedClusterCenters({
			clusterIndices: cluster.indices,
			input: inputProphoto,
			target: targetProphoto,
			pixelWeights,
		})
		prophotoToSrgb(centerSrgb, centerInput)
		prophotoToSrgb(centerTargetSrgb, centerTarget)
		const centerDiff = colorDifference(centerSrgb, centerTargetSrgb)
		const distances = result.map((c) => {
			prophotoToSrgb(centerSrgbDiff, c.center)
			return colorDifference(centerSrgbDiff, centerTargetSrgb)
		})
		const minDistance = distances.length > 0 ? Math.min(...distances) : 0.1
		if (
			minDistance > 0.05 &&
			(centerDiff > diffThreshold ||
				((minDistance > 0.25 || cluster.weightSum > 0.1) && centerDiff > 0.035))
		) {
			result.push({
				...cluster,
				center: centerSrgb,
				centerInputProphoto: centerInput,
				centerTargetProphoto: centerTarget,
				diffToTarget: centerDiff,
			})
		}
	}
	return result
}

function getWeightedClusterCenters({
	clusterIndices,
	input,
	target,
	pixelWeights,
}: {
	clusterIndices: number[]
	input: Float32Array
	target: Float32Array
	pixelWeights: number[]
}): { centerInput: vec3; centerTarget: vec3 } {
	const centerInput: vec3 = [0, 0, 0]
	const centerTarget: vec3 = [0, 0, 0]
	let totalWeight = 0

	clusterIndices.forEach((index) => {
		const weight = pixelWeights[index] ?? 0

		centerInput[0] += (input[index * 3] ?? 0) * weight
		centerInput[1] += (input[index * 3 + 1] ?? 0) * weight
		centerInput[2] += (input[index * 3 + 2] ?? 0) * weight

		centerTarget[0] += (target[index * 3] ?? 0) * weight
		centerTarget[1] += (target[index * 3 + 1] ?? 0) * weight
		centerTarget[2] += (target[index * 3 + 2] ?? 0) * weight

		totalWeight += weight
	})

	if (totalWeight > 0) {
		centerInput[0] /= totalWeight
		centerInput[1] /= totalWeight
		centerInput[2] /= totalWeight
		centerTarget[0] /= totalWeight
		centerTarget[1] /= totalWeight
		centerTarget[2] /= totalWeight
	}
	return { centerInput, centerTarget }
}

export function getWeightedClusterCenter({
	clusterIndices,
	input,
	pixelWeights,
}: {
	clusterIndices: number[]
	input: Float32Array
	pixelWeights: number[]
}): vec3 {
	const centerInput: vec3 = [0, 0, 0]
	let totalWeight = 0

	clusterIndices.forEach((index) => {
		const weight = pixelWeights[index] ?? 0

		centerInput[0] += (input[index * 3] ?? 0) * weight
		centerInput[1] += (input[index * 3 + 1] ?? 0) * weight
		centerInput[2] += (input[index * 3 + 2] ?? 0) * weight

		totalWeight += weight
	})

	if (totalWeight > 0) {
		centerInput[0] /= totalWeight
		centerInput[1] /= totalWeight
		centerInput[2] /= totalWeight
	}
	return centerInput
}

interface PointColorInfo {
	pc: PointColorHSL
	index: number
	isDiverse: boolean
	clusterIndices?: number[]
}

const hsv = vec3.create()
const targetHsv = vec3.create()

function getHSVShift(
	color: vec3,
	targetColor: vec3,
	scale: number,
): { hueShift: number; satShift: number; lumShift: number } | null {
	rgbToHsv(hsv, color)
	rgbToHsv(targetHsv, targetColor)
	const hueScale = 35 / 360
	const satScale = 0.4
	const lumScale = 0.35
	const result = {
		hueShift: clamp((wrapHueDiff(targetHsv[0] - hsv[0]) / hueScale) * scale, -1, 1),
		satShift: clamp(((targetHsv[1] - hsv[1]) / satScale) * scale, -1, 1),
		lumShift: clamp(((targetHsv[2] - hsv[2]) / lumScale) * scale, -1, 1),
	}

	// If the shift is too big, return null because it will probably cause artifacts
	if (
		Math.abs(result.hueShift) > 0.6 ||
		Math.abs(result.satShift) > 0.4 ||
		Math.abs(result.lumShift) > 0.4
	) {
		logColorMatch("[getHSVShift] too big shift", { hsv, targetHsv, result })
		return null
	}

	return result
}

// D65 illuminant reference white
const Xn = 0.95047
const Yn = 1.0
const Zn = 1.08883

function rgbToLab(rgb: vec3): vec3 {
	// Step 1: RGB to XYZ
	// First convert RGB to linear RGB (remove gamma correction)
	let r = rgb[0]
	let g = rgb[1]
	let b = rgb[2]

	// Convert sRGB to linear RGB
	r = r > 0.04045 ? Math.pow((r + 0.055) / 1.055, 2.4) : r / 12.92
	g = g > 0.04045 ? Math.pow((g + 0.055) / 1.055, 2.4) : g / 12.92
	b = b > 0.04045 ? Math.pow((b + 0.055) / 1.055, 2.4) : b / 12.92

	// Convert to XYZ
	const x = (r * 0.4124564 + g * 0.3575761 + b * 0.1804375) / Xn
	const y = (r * 0.2126729 + g * 0.7151522 + b * 0.072175) / Yn
	const z = (r * 0.0193339 + g * 0.119192 + b * 0.9503041) / Zn

	// Step 2: XYZ to Lab
	// Helper function for XYZ to Lab conversion
	const f = (t: number): number => (t > 0.008856 ? Math.pow(t, 1 / 3) : 7.787 * t + 16 / 116)

	const fx = f(x)
	const fy = f(y)
	const fz = f(z)

	const L = 116 * fy - 16
	const a = 500 * (fx - fy)
	const b1 = 200 * (fy - fz)

	return [L, a, b1] as vec3
}

/**
 * Calculate CIE94 color difference between two colors in LAB space
 * @param lab1 First LAB color: L[0,100], a[-128,127], b[-128,127]
 * @param lab2 Second LAB color: L[0,100], a[-128,127], b[-128,127]
 * @returns Delta E value, where:
 *   - 0 = perfect match
 *   - < 2.3 = not perceptible
 *   - 2.3-5 = perceptible through close observation
 *   - 5-10 = perceptible at a glance
 *   - > 10 = colors are more similar than different
 */
function deltaE94(lab1: vec3, lab2: vec3): number {
	// Input validation
	if (lab1[0] < 0 || lab1[0] > 100 || lab2[0] < 0 || lab2[0] > 100) {
		console.warn("deltaE94: L values should be in range [0, 100]")
	}
	if (
		Math.abs(lab1[1]) > 128 ||
		Math.abs(lab1[2]) > 128 ||
		Math.abs(lab2[1]) > 128 ||
		Math.abs(lab2[2]) > 128
	) {
		console.warn("deltaE94: a,b values should be in range [-128, 127]")
	}

	const dL = lab1[0] - lab2[0]
	const da = lab1[1] - lab2[1]
	const db = lab1[2] - lab2[2]

	const c1 = Math.sqrt(lab1[1] * lab1[1] + lab1[2] * lab1[2])
	const c2 = Math.sqrt(lab2[1] * lab2[1] + lab2[2] * lab2[2])
	const dC = c1 - c2

	const dH = Math.sqrt(Math.max(0, da * da + db * db - dC * dC))

	// Weighting factors
	const sL = 1
	const k1 = 0.045
	const k2 = 0.015
	const sC = 1 + k1 * c1
	const sH = 1 + k2 * c1

	return Math.sqrt(Math.pow(dL / sL, 2) + Math.pow(dC / sC, 2) + Math.pow(dH / sH, 2))
}

export function colorDifference(color1: vec3, color2: vec3): number {
	const lab1 = rgbToLab(color1)
	const lab2 = rgbToLab(color2)
	return deltaE94(lab1, lab2) / 100
}

export function createDefaultPointColor(
	srgbColor: [number, number, number],
	hsvShift?: { hueShift: number; satShift: number; lumShift: number },
): PointColorHSL {
	const hsv = RGBToHSV(srgbColor)

	const relativeHSV = getHSVRelativeShiftInRange(hsv)

	const pointColor: PointColorHSL = {
		id: uuid(),
		color: srgbColor,
		hueShift: hsvShift?.hueShift ?? 0,
		satShift: hsvShift?.satShift ?? 0,
		lumShift: hsvShift?.lumShift ?? 0,
		range: 0.5,
		hueRange: getDefaultRelativeShiftRange(relativeHSV[0]),
		satRange: getDefaultRelativeShiftRange(relativeHSV[1]),
		lumRange: getDefaultRelativeShiftRange(relativeHSV[2]),
	}

	return pointColor
}

export function getPointColors({
	toneMapInvBefore,
	target,
	adjustments,
	baseRenderOptions,
	pixelWeights,
}: {
	toneMapInvBefore: Float32Array
	target: Float32Array
	adjustments: Partial<Adjustments>
	baseRenderOptions: RenderOptions
	pixelWeights: number[]
}) {
	const maxColors = 8

	const renderOptions = { ...baseRenderOptions, outColorSpace: ColorSpaceCpu.proPhoto }
	const prophotoImg = renderCpu(toneMapInvBefore, adjustments, {
		...baseRenderOptions,
		inputColorSpace: ColorSpaceCpu.toneMapInversed,
		outColorSpace: ColorSpaceCpu.proPhoto,
	})
	const initialError = getWeightedDiff(prophotoImg, target, pixelWeights)
	let lastError = initialError

	const changedClusters = getChangedColorClusters(
		prophotoImg,
		target,
		pixelWeights,
		maxColors + 2,
	)
	logColorMatch("[point colors] changed clusters", changedClusters)

	const pointColorInfos: PointColorInfo[] = changedClusters.map((cluster) => ({
		pc: createDefaultPointColor(cluster.center),
		index: cluster.indices[0] ?? 0,
		clusterIndices: cluster.indices,
		isDiverse: true,
	}))

	if (pointColorInfos.length === 0) {
		return adjustments
	}

	logColorMatch("[point colors] initial error", initialError)
	// add point colors one by one
	let selectedPCInfos: PointColorInfo[] = []
	let current = prophotoImg
	for (let i = 0; i < pointColorInfos.length; i++) {
		if (selectedPCInfos.length >= maxColors) {
			break
		}
		const info = pointColorInfos[i] as PointColorInfo
		const tempPCInfos = [...selectedPCInfos, info]
		const pc = info?.pc

		const targetColor = getWeightedClusterCenter({
			clusterIndices: info.clusterIndices as number[],
			input: target,
			pixelWeights,
		})
		for (let iterations = 0; iterations < 5; iterations++) {
			const currentColor = getWeightedClusterCenter({
				clusterIndices: info.clusterIndices as number[],
				input: current,
				pixelWeights,
			})
			const scale = 0.7
			const hsvDelta = getHSVShift(currentColor, targetColor, scale)

			// Try to prevent artifacts, especially for low saturation colors
			// Todo: need to improve rendering shader of point colors
			let satMax = 0.35
			let lumMax = 0.1
			if (pc.satRange[1] < 0.1) {
				satMax = 0.1
			}
			if (pc.satRange[1] < 0.05) {
				satMax = 0.05
				lumMax = 0.05
			}
			if (hsvDelta) {
				pc.hueShift = clamp(pc.hueShift + hsvDelta.hueShift, -0.6, 0.6)
				pc.satShift = clamp(pc.satShift + hsvDelta.satShift, -0.4, satMax)
				pc.lumShift = clamp(pc.lumShift + hsvDelta.lumShift, -lumMax, lumMax)
			} else {
				break
			}
			// neglecting small shifts
			if (
				iterations === 0 &&
				Math.abs(hsvDelta.hueShift) < 0.08 &&
				Math.abs(hsvDelta.satShift) < 0.08 &&
				Math.abs(hsvDelta.lumShift) < 0.08 &&
				Math.abs(hsvDelta.hueShift) +
					Math.abs(hsvDelta.satShift) +
					Math.abs(hsvDelta.lumShift) <
					0.15
			) {
				break
			}

			const tempPointColors = tempPCInfos.map((info) => info.pc)
			const currAdjustments = { pointColors: tempPointColors }
			const tempCurrent = renderHSLAndColorGrading(
				prophotoImg,
				currAdjustments,
				renderOptions,
			)
			const error = getWeightedDiff(tempCurrent, target, pixelWeights)

			logColorMatch(
				`[point color ${i}] iteration`,
				iterations,
				", error:",
				error,
				", point color:",
				structuredClone(tempPointColors),
			)
			// allow the error to be slightly larger than the last error if it's the first iteration
			if (iterations === 0) {
				if (error > lastError + 0.0003 || error > initialError) {
					break
				}
			} else {
				if (error > lastError) {
					break
				}
			}
			selectedPCInfos = structuredClone(tempPCInfos)
			lastError = error
			current = tempCurrent
		}
	}

	if (lastError > initialError) {
		return adjustments
	}
	return { ...adjustments, pointColors: selectedPCInfos.map((info) => info.pc) }
}

export function getColorsFromImage(image: ImageBitmap | ImageData | PixelData) {
	let result: { colors: Float32Array; pixelWeights: number[] }
	if (!(image instanceof ImageBitmap)) {
		const { colors, weights: pixelWeights } = extractColors({
			rgbData: getRgbData(image),
			faceColors: null,
			faceWeights: null,
		})
		result = { colors, pixelWeights }
	} else {
		result = getColorAndWeight({
			inputImageBitmap: image,
			faceColors: null,
			faceWeights: null,
		})
	}
	return result
}

export function getDominantColors(result: { colors: Float32Array; pixelWeights: number[] }) {
	const clusters = getInitialColorClusters({
		inputProphoto: result.colors,
		pixelWeights: result.pixelWeights,
		threshold: 0.1,
		inputColorSpace: ColorSpaceCpu.sRGB,
		satThreshold: 0.0,
		lumThreshold: 0.0,
	})

	const colorPatches: vec4[] = clusters
		.filter((cluster) => cluster.weightSum > 0.025)
		.map((cluster) => [...cluster.center, cluster.weightSum])
	// normalize color patches weights
	const totalWeight = colorPatches.reduce((acc, patch) => acc + patch[3], 0)
	colorPatches.forEach((patch) => {
		patch[3] /= totalWeight
	})
	return colorPatches
}

export function getWhiteColor(options: {
	colors: Float32Array
	pixelWeights: number[]
}): vec3 | null {
	const result = getWhiteColors(options)
	if (!result) {
		return null
	}
	const whiteColor = getWeightedMeanRgb(result.whiteColors, result.whiteWeights, 0.0001)
	return whiteColor
}

export function getGrayColors(options: { colors: Float32Array; pixelWeights: number[] }): {
	whiteColors: Float32Array
	whiteWeights: Float32Array
	indices: number[]
	totalWeight: number
} | null {
	const { colors, pixelWeights } = options
	const result = getWhiteColors({
		colors,
		pixelWeights,
		strictWhite: false,
		lumThreshold: 0.1,
		satThreshold: 0.25,
	})
	return result
}

export function getGrayColorMean(options: { colors: Float32Array; pixelWeights: number[] }) {
	const result = getGrayColors(options)
	if (!result) {
		return null
	}
	const indices = getGrayPixelIndices(result.whiteColors, 0.25)
	return getGrayMeanRgbs(result.whiteColors, indices, result.whiteWeights)
}

export function getWhiteColors(options: {
	colors: Float32Array
	pixelWeights: number[]
	lumThreshold?: number
	satThreshold?: number
	strictWhite?: boolean
}): {
	whiteColors: Float32Array
	whiteWeights: Float32Array
	indices: number[]
	totalWeight: number
} | null {
	const {
		colors,
		pixelWeights,
		lumThreshold = 0.7,
		satThreshold = 0.14,
		strictWhite = true,
	} = options
	const whiteColorsAll: number[] = []
	const whiteWeightsAll: number[] = []
	const indicesAll: number[] = []
	let totalWeight = 0
	for (let i = 0; i < colors.length / 3; i++) {
		const j = i * 3
		const [r, g, b] = [colors[j], colors[j + 1], colors[j + 2]] as vec3
		const maxRgb = Math.max(r, g, b)
		const minRgb = Math.min(r, g, b)
		const lum = (maxRgb + minRgb) / 2
		const sat = maxRgb === 0 ? 0 : (maxRgb - minRgb) / maxRgb
		const weight = pixelWeights[i] ?? 0
		if (sat < 0.04 && lum > 0.9) {
			// exclude almost clamped white colors
			continue
		}
		if (strictWhite) {
			if (g > r * 0.5 + b * 0.5 + 0.03) {
				// exclude green-dominated white colors as they are likely vegetations
				continue
			}
			if (r < g * 0.5 + b * 0.5 - 0.03) {
				// exclude blue-green-dominated white colors as they are likely blue sky or clothes
				continue
			}
		}
		if (lum > lumThreshold && sat < satThreshold) {
			whiteColorsAll.push(r)
			whiteColorsAll.push(g)
			whiteColorsAll.push(b)
			whiteWeightsAll.push(weight)
			totalWeight += weight
			indicesAll.push(i)
		}
	}
	if (totalWeight > 0.001) {
		const weightThreshold = 0.01
		if (totalWeight > weightThreshold) {
			// sort the colors by saturation, and only take the lowest 40% and up to sat = 0.06
			const saturations = applyTransformToOneDimension(
				new Float32Array(whiteColorsAll),
				rgbToSat,
			)
			const whiteIndices = Array.from({ length: saturations.length }, (_, i) => i)
			const sortedIndices = whiteIndices.sort(
				(a, b) => (saturations[a] ?? 0) - (saturations[b] ?? 0),
			)
			let selectedTotalWeight = 0
			const whiteColors: number[] = []
			const whiteWeights: number[] = []
			const indices: number[] = []
			for (let i = 0; i < sortedIndices.length; i++) {
				const index = sortedIndices[i] as number
				const sat = saturations[index] ?? 0
				const [r, g, b] = [
					whiteColorsAll[index * 3],
					whiteColorsAll[index * 3 + 1],
					whiteColorsAll[index * 3 + 2],
				] as vec3

				const weight = whiteWeightsAll[index] ?? 0
				selectedTotalWeight += weight

				whiteColors.push(r)
				whiteColors.push(g)
				whiteColors.push(b)
				whiteWeights.push(weight)
				indices.push(indicesAll[index] as number)

				if (
					selectedTotalWeight > Math.max(0.4 * totalWeight, weightThreshold) &&
					sat > 0.05
				) {
					logColorMatch("[get white colors]", {
						selectedTotalWeight,
						totalWeight,
						satThreshold: saturations[index],
					})
					break
				}
			}

			return {
				whiteColors: new Float32Array(whiteColors),
				whiteWeights: new Float32Array(whiteWeights),
				indices,
				totalWeight: selectedTotalWeight,
			}
		}

		return {
			whiteColors: new Float32Array(whiteColorsAll),
			whiteWeights: new Float32Array(whiteWeightsAll),
			indices: indicesAll,
			totalWeight,
		}
	} else {
		return null
	}
}

export function getClusterColorsAndWeights(options: {
	input: Float32Array
	pixelWeights: number[]
	cluster: ColorCluster
}) {
	const { input, pixelWeights, cluster } = options
	const colors = new Float32Array(cluster.indices.length * 3)

	for (let i = 0; i < cluster.indices.length; i++) {
		const index = cluster.indices[i] as number

		colors[i * 3] = input[index * 3] ?? 0
		colors[i * 3 + 1] = input[index * 3 + 1] ?? 0
		colors[i * 3 + 2] = input[index * 3 + 2] ?? 0
	}

	return {
		colors,
		pixelWeights: cluster.indices.map((index) => pixelWeights[index] ?? 0),
	}
}
